import React, { ReactNode, ComponentType } from 'react';
import { Dropdown } from 'react-bootstrap';

import BayTooltip from '../BayTooltip';

import ActionButton from './ActionButton';

export interface DropdownItem {
	eventKey: string;
	label: string | React.ReactElement | React.ReactNode;
	onClick?: (item: DropdownItem, entity?: any) => void;
	keepMenuOpen?: boolean;
	disabled?: boolean;
	tooltip?: string;
	tooltipMaxWidth?: number | string;
	tooltipPlacement?:
		| 'top'
		| 'left'
		| 'right'
		| 'bottom'
		| 'topLeft'
		| 'topRight'
		| 'bottomLeft'
		| 'bottomRight'
		| 'leftTop'
		| 'leftBottom'
		| 'rightTop'
		| 'rightBottom';
	icon?: React.ReactElement;
	divider?: boolean;
	isActive?: boolean;
	textcolor?: string;
}

export interface BayDropdownProps {
	children?: ReactNode;
	/** Custom component for rendering dropdown menu items. Receives entity and items props. */
	menuComponent?: ComponentType<{ entity?: any; items: DropdownItem[] }>;
	/**
	 * If we need use dropdown as an action list to do something
	 * with data, you can pass data through 'entity' param.
	 */
	entity?: any;
	items: DropdownItem[];
	onSelect?: (eventKey: any, e?: React.SyntheticEvent) => void;
	translate?: (key: string) => string;
	className?: string;
	onHandleOpen?: () => void;
	togglePreventDefault?: boolean;
	/**
	 * "start" | "end" | { sm: "start" | "end" } | { md: "start" | "end" } | { lg: "start" | "end" } | { xl: "start" | "end"} | { xxl: "start" | "end"}
	 * @default "start"
	 */
	alignment?: 'start' | 'end';
	dropdownMenuClassname?: string;
	/** Accessible name for the dropdown toggle control (e.g. a kebab button). Optional — when omitted, the toggle renders exactly as before. */
	toggleAriaLabel?: string;
}

// togglePreventDefault/onHandleOpen/toggleAriaLabel are threaded through as
// props (not closed over) -- react-bootstrap's DropdownToggle spreads extra
// props onto the `as` component.
const CustomToggle = React.forwardRef<
	HTMLDivElement,
	{
		children?: ReactNode;
		style?: React.CSSProperties;
		onClick?: (e: React.MouseEvent) => void;
		togglePreventDefault?: boolean;
		onHandleOpen?: () => void;
		toggleAriaLabel?: string;
			}
			>(({ children, style, onClick, togglePreventDefault, onHandleOpen, toggleAriaLabel }, ref) => (
				<div
					ref={ref}
					style={style}
					{...(toggleAriaLabel && { role: 'button', 'aria-label': toggleAriaLabel })}
					onClick={(e) => {
						if (togglePreventDefault) {
							e.preventDefault();
						} else {
							e.stopPropagation();
						}
						onClick?.(e);
						onHandleOpen?.();
					}}
				>
					{children}
				</div>
			));

//NOTE: https://github.com/react-bootstrap/react-bootstrap/issues/6619
//NOTE: https://github.com/react-bootstrap/react-bootstrap/issues/6750
const BayDropdown: React.FC<BayDropdownProps> = ({
	translate,
	children,
	menuComponent = null,
	items,
	entity,
	onSelect = () => {},
	className = '',
	onHandleOpen = () => {},
	alignment = 'start',
	togglePreventDefault = true,
	dropdownMenuClassname = '',
	toggleAriaLabel,
}) => {
	const getItems = () => {
		return items?.map((item, index) => {
			// Element, not a component: same remount risk as CustomToggle above.
			const itemContent = (
				<>
					{item?.icon && <div className="me-2 text-700">{item.icon}</div>}
					{item?.label && (
						<div
							className={`ms-1 ${item?.textcolor ? item?.textcolor : 'text-700'}`}
						>
							{item.label}
						</div>
					)}
				</>
			);

			return (
				<div key={index}>
					<Dropdown.Item
						as="div"
						className={`${item?.isActive ? 'active' : ''} d-flex justify-content-start align-items-center cursor-pointer px-3 py-2`}
						disabled={item?.disabled}
						eventKey={item?.eventKey}
						role="menuitem"
						style={{ pointerEvents: 'auto' }}
						onClick={(e) => {
							if (item?.keepMenuOpen) {
								e.preventDefault();
								e.stopPropagation();
							}
							if (item?.onClick) {
								item.onClick(item, entity);
							}
						}}
					>
						{item?.tooltip ? (
							<BayTooltip
								overlay={item?.tooltip}
								overlayMaxWidth={item?.tooltipMaxWidth}
								placement={item?.tooltipPlacement || 'top'}
							>
								<div className="d-flex align-items-center">
									{itemContent}
								</div>
							</BayTooltip>
						) : (
							itemContent
						)}
					</Dropdown.Item>
					{item?.divider && <Dropdown.Divider className="my-3" />}
				</div>
			);
		});
	};

	const renderDropdownItems = () => {
		// Use custom menu component if provided
		if (menuComponent) {
			const MenuComponent = menuComponent;
			return <MenuComponent entity={entity} items={items} />;
		}

		// Use default items rendering
		return getItems();
	};

	// Memoize the rendered menu content to avoid unnecessary recalculations
	// Only recalculates when menuComponent, items, or entity changes
	const memoizedMenuContent = React.useMemo(() => {
		return renderDropdownItems();
	}, [menuComponent, items, entity]);

	const MemoizedDropdown = React.useMemo(() => (
		<Dropdown
			align={alignment}
			autoClose
			className={`${className}`}
			onSelect={(eventKey, e) => onSelect(eventKey, e as any)}
		>
			<Dropdown.Toggle
				as={CustomToggle}
				onHandleOpen={onHandleOpen}
				toggleAriaLabel={toggleAriaLabel}
				togglePreventDefault={togglePreventDefault}
			>
				{children ? (
					<div
						onClick={() => {
							onHandleOpen();
						}}
					>
						{children}
					</div>
				) : (
					<div>
						<ActionButton translate={translate} variant="menu" onClick={() => {}} isActive={false} customTooltip={undefined} />
					</div>
				)}
			</Dropdown.Toggle>
			<Dropdown.Menu className={dropdownMenuClassname} renderOnMount={false} role="menu">
				{memoizedMenuContent}
			</Dropdown.Menu>
		</Dropdown>
	// Only re-render if these dependencies change
	), [
		alignment,
		className,
		onSelect,
		children,
		onHandleOpen,
		translate,
		dropdownMenuClassname,
		memoizedMenuContent,
		togglePreventDefault,
		toggleAriaLabel,
	]);

	return MemoizedDropdown;
};

export default BayDropdown;
